import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import {
  LayoutDashboard,
  Package,
  Users,
  BadgePercent,
  TrendingUp,
  Settings,
  LogOut,
  Menu,
  X,
  Search,
  UserCog,
} from "lucide-react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Input } from "@/components/ui/input";
import { customerService } from "@/lib/database";
import { Toaster } from "@/components/ui/sonner";
import { ShipmentProvider } from "@/contexts/shipment-context";

const appVersion = import.meta.env.VITE_APP_VERSION;

interface AdminLayoutProps {
  children: React.ReactNode;
}

export default function AdminLayout({ children }: AdminLayoutProps) {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const [searchQuery, setSearchQuery] = useState("");
  const [_, setSearchResults] = useState<any>(null);
  const [isSearching, setIsSearching] = useState(false);

  const navigation = [
    { name: "Dashboard", href: "/admin/dashboard", icon: LayoutDashboard },
    { name: "Shipments", href: "/admin/shipments", icon: Package },
    {
      name: "Discounts & Promos",
      href: "/admin/discounts-promos",
      icon: BadgePercent,
    },
    { name: "Customers", href: "/admin/customers", icon: Users },
    { name: "Analytics", href: "/admin/analytics", icon: TrendingUp },
    { name: "Team", href: "/admin/team", icon: UserCog },
    { name: "Settings", href: "/admin/settings", icon: Settings },
  ];

  const handleLogout = () => {
    localStorage.removeItem("userRole");
    localStorage.removeItem("userId");
    localStorage.removeItem("userFullName");
    localStorage.removeItem("userEmail");
    navigate("/");
  };

  useEffect(() => {
    // Check if user is authenticated and has admin role
    const userRole = localStorage.getItem("userRole");
    const userFullName = localStorage.getItem("userFullName");
    const userEmail = localStorage.getItem("userEmail");

    const returnPath = encodeURIComponent(location.pathname + location.search);

    // If not admin, redirect to login
    if (!userRole || userRole !== "admin" || !userFullName || !userEmail) {
      navigate(`/auth/login?r=${returnPath}`);
    }
  }, []);

  const handleSearch = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!searchQuery.trim()) return;

    setIsSearching(true);
    try {
      const results = await customerService.searchCustomersAndShipments(
        searchQuery.trim()
      );
      setSearchResults(results);

      // Navigate to search results page (we'll create this)
      navigate("/admin/search", {
        state: { query: searchQuery.trim(), results },
      });
    } catch (error) {
      console.error("Search error:", error);
    } finally {
      setIsSearching(false);
    }
  };

  return (
    <ShipmentProvider>
      <div className="min-h-screen bg-gray-50">
        {/* Mobile sidebar overlay */}
        {sidebarOpen && (
          <div className="fixed inset-0 z-40 lg:hidden">
            <div
              className="fixed inset-0 bg-gray-600 bg-opacity-75"
              onClick={() => setSidebarOpen(false)}
            />
          </div>
        )}

        {/* Sidebar - Fixed position */}
        <div
          className={`fixed inset-y-0 left-0 z-50 w-64 bg-white shadow-lg transform ${
            sidebarOpen ? "translate-x-0" : "-translate-x-full"
          } transition-transform duration-300 ease-in-out lg:translate-x-0`}
        >
          <div className="flex flex-col h-full">
            <div className="flex items-center justify-center h-16 px-6 border-b border-gray-200 flex-shrink-0">
              <div className="flex items-center justify-center">
                {/* <img
                src="/108shippingservices-logo-filled-rect.jpg"
                alt="108 Shipping & Services"
                className="w-8 h-8"
              /> */}
                <Link to="/">
                  <img
                    src="/108shippingservices-logo-trans.png"
                    alt="108 Shipping & Services"
                    className="w-40 h-40"
                  />
                </Link>
                {/* <div>
                <h1 className="text-sm font-bold text-[#254164]">108 SHIPPING</h1>
                <p className="text-xs text-gray-600">Admin Panel</p>
              </div> */}
              </div>
              <button
                className="lg:hidden"
                onClick={() => setSidebarOpen(false)}
              >
                <X className="h-6 w-6 text-gray-400" />
              </button>
            </div>

            <nav className="flex-1 px-3 py-6 overflow-y-auto mt-2">
              <div className="space-y-1">
                {navigation.map((item) => {
                  const isActive = location.pathname.startsWith(item.href);

                  return (
                    <Link
                      key={item.name}
                      to={item.href}
                      className={`group flex items-center px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                        isActive
                          ? "bg-[#254164] text-white"
                          : "text-gray-700 hover:bg-gray-100 hover:text-gray-900"
                      }`}
                    >
                      <item.icon
                        className={`mr-3 h-5 w-5 ${
                          isActive
                            ? "text-white"
                            : "text-gray-400 group-hover:text-gray-500"
                        }`}
                      />
                      {item.name}
                    </Link>
                  );
                })}
              </div>
            </nav>

            <div className="flex justify-center items-center mb-2">
              <span className="text-xs text-gray-500">Platform Version:</span>
              <span className="ml-1 text-xs text-gray-500 font-bold">
                {appVersion}
              </span>
            </div>

            <div className="flex-shrink-0 p-3 border-t border-gray-200">
              <Button
                onClick={handleLogout}
                variant="ghost"
                className="w-full justify-start text-gray-700 hover:bg-gray-100"
              >
                <LogOut className="mr-3 h-5 w-5" />
                Sign Out
              </Button>
            </div>
          </div>
        </div>

        {/* Main content - Adjusted for fixed sidebar */}
        <div className="lg:pl-64">
          {/* Top navigation - Fixed position */}
          <div className="sticky top-0 z-30 bg-white shadow-sm border-b border-gray-200">
            <div className="flex items-center justify-between h-16 px-4 sm:px-6 lg:px-8">
              <button
                className="lg:hidden"
                onClick={() => setSidebarOpen(true)}
              >
                <Menu className="h-6 w-6 text-gray-400" />
              </button>

              <div className="flex items-center space-x-4 ml-auto">
                <form onSubmit={handleSearch} className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                  <Input
                    placeholder="Search customers, tracking numbers..."
                    className="pl-10 w-full"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    disabled={isSearching}
                  />
                </form>

                {/* <Button variant="ghost" size="sm" className="relative">
                <Bell className="h-5 w-5" />
                <Badge className="absolute -top-1 -right-1 h-5 w-5 rounded-full bg-red-500 text-white text-xs flex items-center justify-center p-0">
                  3
                </Badge>
              </Button> */}

                <div className="flex items-center space-x-3">
                  <div className="text-right hidden sm:block">
                    <p className="text-sm font-medium text-gray-900">
                      {localStorage.getItem("userFullName")}
                    </p>
                    <p className="text-xs text-gray-500">
                      {localStorage.getItem("userEmail")}
                    </p>
                  </div>
                  <div className="h-8 w-8 bg-[#254164] rounded-full flex items-center justify-center">
                    <span className="text-white text-sm font-medium">
                      {localStorage.getItem("userFullName")?.charAt(0)}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Page content */}
          <main className="p-4 sm:p-6 lg:p-8">{children}</main>

          <Toaster />
        </div>
      </div>
    </ShipmentProvider>
  );
}
